// =====================================================================
// PropertyCuts — Render orchestration (client side)
// =====================================================================
// Exports are created by the start_propertycut_renders RPC, which checks
// ownership, prices on the server, debits once and queues one render per
// format in a single transaction. The price computed here is only a
// preview; it is sent as `expected credits` so a price change is refused
// rather than silently charged.
// =====================================================================

import { supabase } from './supabase';
import type { PropertyCutRender, RenderFormat } from '../types';
import type { RenderCostBreakdown } from './renderPricing';

export { computeRenderCost, type RenderCostBreakdown } from './renderPricing';

// ---------------------------------------------------------------------
// Create + charge
// ---------------------------------------------------------------------

interface CreateRenderArgs {
  projectId: string;
  userId: string;
  formats: RenderFormat[];
  /** Pass the cost breakdown the user just confirmed in the UI */
  cost: RenderCostBreakdown;
  /** Reuse for retries of the same request so it is charged once. */
  idempotencyKey?: string;
}

export type CreateRenderError =
  | 'INSUFFICIENT_CREDITS'
  | 'PRICE_CHANGED'
  | 'MUSIC_SOURCE_REMOVED'
  | 'INSERT_FAILED'
  | 'CHARGE_FAILED'
  | 'UNKNOWN';

/** Server refusals, in the order they are checked. Nothing is charged for any of them. */
const REFUSALS: Array<[CreateRenderError, string]> = [
  ['INSUFFICIENT_CREDITS', 'Not enough credits for this export.'],
  ['PRICE_CHANGED', 'The export price has changed. Please review and try again.'],
  ['MUSIC_SOURCE_REMOVED', 'A music track on this video is no longer available. Remove it and choose a royalty-free track or upload your own.'],
];

export interface CreateRenderResult {
  success: boolean;
  error?: CreateRenderError;
  errorMessage?: string;
  renders: PropertyCutRender[];
  creditsRemaining?: number;
}

/**
 * Charge and queue one render per selected format in a single server-side
 * transaction. Retrying with the same idempotency key returns the renders the
 * first attempt created and charges nothing more.
 */
export async function createRender(args: CreateRenderArgs): Promise<CreateRenderResult> {
  const { data, error } = await supabase.rpc('start_propertycut_renders', {
    p_project_id: args.projectId,
    p_formats: args.formats,
    p_idempotency_key: args.idempotencyKey ?? `export:${crypto.randomUUID()}`,
    p_expected_credits: args.cost.total,
  });
  if (error) {
    const message = error.message || '';
    const refusal = REFUSALS.find(([code]) => message.includes(code));
    if (refusal) return { success: false, error: refusal[0], errorMessage: refusal[1], renders: [] };
    return { success: false, error: 'CHARGE_FAILED', errorMessage: 'Could not start the export.', renders: [] };
  }
  const renders = ((data?.renders ?? []) as PropertyCutRender[]);
  return {
    success: true,
    renders,
    creditsRemaining: typeof data?.credits_remaining === 'number' ? data.credits_remaining : undefined,
  };
}

/** Gives up on a stuck export (queued over 3 minutes or rendering over 20) and refunds it once. */
export async function cancelStalledRender(renderId: string): Promise<{ success: boolean; error?: string }> {
  const { data, error } = await supabase.rpc('cancel_stalled_propertycut_render', { p_render_id: renderId });
  if (error) return { success: false, error: error.message.includes('NOT_STALLED') ? 'NOT_STALLED' : 'FAILED' };
  return { success: true, error: data?.success ? undefined : data?.error };
}
